import { useMemo } from "react";

/**
 * Returns the last (most recent) navigation entry that satisfies the given predicate.
 *
 * @param predicate - Function that takes a pathname and returns true if it satisfies the condition
 * @param defaultValue - Value to return if no entry satisfies the predicate (defaults to "/")
 * @returns The full path (pathname + search + hash) of the last matching entry
 *
 * @example
 * // Get the last visited non-settings page
 * const lastNonSettingsPage = useLastNavigationEntry(
 *   (pathname) => !pathname.startsWith("/settings")
 * );
 */
export const useLastNavigationEntry = (predicate: (pathname: string) => boolean, defaultValue = "/"): string => {
    return useMemo(() => {
        if (!window.navigation) {
            return defaultValue;
        }

        // navigation.entries goes from oldest to newest, so we reverse to get most recent first
        const entries = window.navigation.entries().reverse();
        for (const entry of entries) {
            if (!entry.url) {
                continue;
            }

            try {
                const url = new URL(entry.url);
                if (predicate(url.pathname)) {
                    return url.pathname + url.search + url.hash;
                }
            } catch {
                continue;
            }
        }

        return defaultValue;
    }, [predicate, defaultValue]);
};
